import { Component, computed, input, OnInit, Signal, signal, WritableSignal, ViewChild, Input, Output, EventEmitter, ElementRef, AfterViewInit, effect, OnDestroy, Renderer2 } from '@angular/core';
import { ModalController, IonPopover, IonContent, ViewDidEnter } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CalendarEvent, UnleashFeatureFlags, DATETIMEFORMART } from 'src/app/interfaces/interfaces';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
import { EventComponent } from 'src/app/shared/event/event.component';
import { ViewEditBookingComponent } from 'src/app/shared/view-edit-booking/view-edit-booking.component';
import { UserStore } from 'src/app/stores/user.store';
import { SwiperContainer } from 'swiper/element';
import { SwiperCalendarComponent } from 'src/app/shared/swiper-calendar/swiper-calendar.component';

interface CalendarEventWithPosition extends CalendarEvent {
  position: {
    top: string;
    height: string;
    left: string;
    width: string;
  };
  zIndex?: number;
}

@Component({
  selector: 'app-daily-calendar-modal',
  templateUrl: './daily-calendar-modal.page.html',
  styleUrls: ['./daily-calendar-modal.page.scss'],
})
export class DailyCalendarModalPage implements OnInit, OnDestroy, ViewDidEnter, AfterViewInit {
  // Input signals
  events = input<CalendarEvent[]>([]);
  @Input() public isFromBookingsPage: boolean;
  @Output() selectedDateChange = new EventEmitter<string>();
  selectedDate: WritableSignal<Date> = signal(new Date());
  
  // Swiper references
  @ViewChild('daySwiper') daySwiperRef!: ElementRef<SwiperContainer>;
  @ViewChild(SwiperCalendarComponent) swiperCalendarComponent!: SwiperCalendarComponent;
  @ViewChild('eventsList', { read: ElementRef }) eventsListRef!: ElementRef<HTMLElement>;
  
  // Day slides for swiper (with pre-computed events to avoid function calls in template)
  daySlides: Array<{
    date: Date;
    dateString: string;
    events: any[];
    allDayEvents: any[];
    isToday: boolean;
  }> = [];

  locale = this.dateTimeFormatService.selectedDateTimeFormat;
  
  // Time range for the calendar (5 AM to 11 PM)
  startHour = 0;
  endHour = 23;
  
  // Slot height for consistent positioning
  slotHeight = 70; // Default height
  
  // Computed signals
  currentTime: WritableSignal<Date> = signal(new Date());
  unleashFeatureFlags = UnleashFeatureFlags;
  currentTimePosition: WritableSignal<number> = signal(-1);

  private currentTimeRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly currentTimeRefreshMs = 10_000;
  
  // Current time position with px suffix 
  currentTimePositionPx: Signal<string> = computed(() => {
    const position = this.currentTimePosition();
    return position >= 0 ? `${position}px` : '';
  });
  
  // Formatted current time 
  formattedCurrentTime: Signal<string> = computed(() => {
    return moment(this.currentTime()).format('HH:mm');
  });
  
  // Check if there are no events 
  hasNoEvents: Signal<boolean> = computed(() => {
    return this.selectedDateEvents().length === 0;
  });
  
  // Week days for mini calendar
  weekDays: Signal<any[]> = computed(() => this.generateWeekDays());
  
  // Events for selected date
  selectedDateEvents: Signal<any[]> = computed(() => this.getEventsForDate(this.selectedDate(), this.events()));
  
  // Update slide events when events change (to avoid function calls in template)
  private updateSlideEvents() {
    if (this.daySlides.length === 0) return;
    
    const today = moment().startOf('day');
    const allEvents = this.events();
    
    this.daySlides.forEach(slide => {
      slide.events = this.getEventsForDate(slide.date, allEvents).filter(
        (e) => !e.isAllDay
      );

      slide.allDayEvents = this.getAllDayEvents(slide.date, allEvents);
      slide.isToday = moment(slide.date).isSame(today, 'day');
    });
  }
  
  // Time slots for the calendar
  timeSlots: Signal<any[]> = computed(() => this.generateTimeSlots());
  
  // Check if today is selected
  isTodaySelected: Signal<boolean> = computed(() => {
    const selectedDate = this.selectedDate();
    const today = moment().startOf('day');
    return moment(selectedDate).isSame(today, 'day');
  });

  constructor(
    private modalController: ModalController,
    private dateTimeFormatService: DatetimeFormatService,
    private translateService: TranslateService,
    private unleashService: UnleashService,
    private userStore: UserStore,
  ) {
    // Update slide events when events input changes
    effect(() => {
      const events = this.events();
      if (this.daySlides.length > 0) {
        this.updateSlideEvents();
      }
    });
  }

  ngOnInit() {
    this.refreshCurrentTimeIndicator();
    this.generateDaySlides();
  }
  
  ngAfterViewInit() {
    this.startCurrentTimeRefresh();
    // Initialize swiper to show current selected date
    setTimeout(() => {
      this.syncSwiperToSelectedDate();
      this.configureSwiperForVerticalScrolling();
      // Highlight today's date in swiper-calendar on initialization
      this.highlightSelectedDateInSwiperCalendar();
    }, 100);
  }

  ngOnDestroy() {
    this.stopCurrentTimeRefresh();
  }
  
  private highlightSelectedDateInSwiperCalendar() {
    if (this.swiperCalendarComponent) {
      const selectedDateString = moment(this.selectedDate()).format('YYYY-MM-DD');
      this.swiperCalendarComponent.highlightDate(selectedDateString, false);
    }
  }
  
  private configureSwiperForVerticalScrolling() {
    if (!this.daySwiperRef?.nativeElement?.swiper) return;
    
    const swiper = this.daySwiperRef.nativeElement.swiper;
    
    // Configure swiper to allow vertical scrolling by only responding to horizontal gestures
    if (swiper.params) {
      // Only respond to clearly horizontal swipes
      swiper.params.threshold = 30; // Minimum distance in px to trigger slide
      swiper.params.touchAngle = 30; // Only respond to swipes within 30 degrees of horizontal
      swiper.params.longSwipesRatio = 0.5;
      swiper.params.longSwipesMs = 300;
      swiper.update();
    }
  }

  ionViewDidEnter() {
    this.startCurrentTimeRefresh();
    // Ensure swiper-calendar highlights the selected date when modal is fully entered
    setTimeout(() => {
      this.highlightSelectedDateInSwiperCalendar();
    }, 150);
    
    if (this.isTodaySelected() && this.currentTimePosition() >= 0) {
        this.scrollToCurrentTime();
    }
  }

  ionViewDidLeave() {
    this.stopCurrentTimeRefresh();
  }

  private refreshCurrentTimeIndicator(): void {
    this.currentTime.set(new Date());
    this.currentTimePosition.set(this.calculateCurrentTimePosition());
  }

  private startCurrentTimeRefresh(): void {
    this.stopCurrentTimeRefresh();
    this.refreshCurrentTimeIndicator();
    this.currentTimeRefreshInterval = setInterval(() => {
      this.refreshCurrentTimeIndicator();
    }, this.currentTimeRefreshMs);
  }

  private stopCurrentTimeRefresh(): void {
    if (this.currentTimeRefreshInterval !== null) {
      clearInterval(this.currentTimeRefreshInterval);
      this.currentTimeRefreshInterval = null;
    }
  }

  private async scrollToCurrentTime() {
    if (!this.content) return;

    const scrollElement = await this.content.getScrollElement();
    if (!scrollElement) return;

    // Get the viewport height
    const viewportHeight = scrollElement.clientHeight;

    const miniWeekHeight = 60; // Approximate height of mini-week header (padding + content)
    const eventAreaMargin = 15; // Top margin of event-area
    
    const eventAreaTopOffset = miniWeekHeight + eventAreaMargin;
    
    // Calculate scroll position: current time position + offset to event-area - half viewport to center
    const scrollPosition = this.currentTimePosition() + eventAreaTopOffset - (viewportHeight / 2);
    
    // Ensure we don't scroll beyond the bounds
    const maxScroll = Math.max(0, scrollElement.scrollHeight - viewportHeight);
    const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));
    
    this.content.scrollToPoint(0, finalScrollPosition, 300); // 300ms smooth scroll
  }


  private generateWeekDays(): any[] {
    const today = moment().startOf('day');
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = moment(today).add(i, 'days');
      days.push({
        name: day.format('ddd'),
        number: day.format('D'),
        date: day.toDate(),
        isSelected: day.isSame(this.selectedDate(), 'day')
      });
    }
    
    return days;
  }

  private generateTimeSlots(): any[] {
    const slots = [];
    for (let hour = this.startHour; hour <= this.endHour; hour++) {
      // Add hour mark
      const hourPosition = (hour - this.startHour) * this.slotHeight;
      slots.push({
        hour: hour,
        time: `${hour.toString().padStart(2, '0')}:00`,
        displayTime: `${hour.toString().padStart(2, '0')}:00`,
        position: hourPosition,
        positionPx: `${hourPosition}px`,
        isHalfHour: false
      });
      
      // Add 30-minute mark (except for the last hour)
      if (hour <= this.endHour) {
        const halfHourPosition = (hour - this.startHour) * this.slotHeight + (this.slotHeight / 2);
        slots.push({
          hour: hour,
          time: `${hour.toString().padStart(2, '0')}:30`,
          displayTime: `${hour.toString().padStart(2, '0')}:30`,
          position: halfHourPosition,
          positionPx: `${halfHourPosition}px`,
          isHalfHour: true
        });
      }
    }
    
    // Add 00:00 (midnight) after 23:00
    const midnightPosition = (24 - this.startHour) * this.slotHeight;
    slots.push({
      hour: 24, // Use 24 to represent midnight of next day
      time: '00:00',
      displayTime: '00:00',
      position: midnightPosition,
      positionPx: `${midnightPosition}px`,
      isHalfHour: false
    });
    
    // Sort slots by position
    return slots.sort((a, b) => a.position - b.position);
  }

  private getEventsForDate(date: Date, events: CalendarEvent[]): any[] {
    // Use local time for consistent timezone handling
    const startOfDay = moment(date).startOf('day');
    const endOfDay = moment(date).endOf('day');

    // First, filter events for the selected date
    const filteredEvents = events.filter(event => {
      // if (event.isExpired) return false;
      
      const eventStart = moment(event.startTime);
      const eventEnd = moment(event.endTime);
      
      // Check if event overlaps with the selected date
      return eventStart.isBefore(endOfDay) && eventEnd.isAfter(startOfDay);
    }).sort((a, b) => moment(a.startTime).diff(moment(b.startTime)));

    // Calculate positions for all events in one pass to handle overlaps correctly
    const eventsWithPositions = this.calculateEventPositions(filteredEvents, date);

    const currentLocale = this.locale();
    
    return eventsWithPositions.map((event: CalendarEventWithPosition) => {
      const isUnrespondedInvitation = this.isUnrespondedInvitation(event);
      const startTimeFormatted = moment(event.startTime).format('HH:mm');
      const endTimeFormatted = moment(event.endTime).format('HH:mm');
      const formattedTimeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
      
      return {
        ...event,
        color: event.isOrganizer ? '#4CAF50' : '#ed143d',
        backgroundColor: event.isOrganizer ? '#4CAF5022' : '#ed143d22',
        formattedTime: formattedTimeRange,
        startTimeFormatted: startTimeFormatted,
        endTimeFormatted: endTimeFormatted,
        location: event.place?.displayName || 'No location',
        left: event.position.left,
        width: event.position.width,
        height: event.position.height,
        top: event.position.top,
        showTime: this.shouldShowEventTime(event),
        showLocation: this.shouldShowEventLocation(event),
        isUnrespondedInvitation: isUnrespondedInvitation,
      }
    });
  }

  private calculateEventPositions(events: CalendarEvent[], date?: Date): CalendarEventWithPosition[] {
    if (!events || events.length === 0) return [];
    
    // Use provided date or fall back to selectedDate
    const targetDate = date || this.selectedDate();

    // Hybrid sorting: Sort by start time first (more intuitive), then by duration (longer first for same start time)
    // This ensures events are processed in chronological order while prioritizing longer events
    const eventsSorted = [...events].sort((a, b) => {
      const aStart = moment(a.startTime);
      const aEnd = moment(a.endTime);
      const bStart = moment(b.startTime);
      const bEnd = moment(b.endTime);
      const timeDiff = aStart.valueOf() - bStart.valueOf();
      if (timeDiff !== 0) return timeDiff;
      // If same start time, longer events first (they need columns earlier)
      const aDur = aEnd.diff(aStart, 'minutes');
      const bDur = bEnd.diff(bStart, 'minutes');
      return bDur - aDur;
    });

    const baseLeftOffset = 40;
    const containerWidth = window.innerWidth - 20;
    const availableWidth = containerWidth - baseLeftOffset;
    const indentWidth = 8;

    // Step 1: Assign columns using the smart "first-hour overlap rule" from daily calendar
    // This prevents unnecessary column splitting - events only get different columns if they overlap
    // within the first hour of the earlier event
    const columnById = new Map<string, number>();
    for (const ev of eventsSorted) {
      let col = 0;
      while (true) {
        const conflict = Array.from(columnById.entries()).some(([otherId, otherCol]) => {
          if (otherCol !== col) return false;
          const other = events.find(e => e.id === otherId)!;
          return this.isOverlapWithinFirstHour(ev, other);
        });
        if (!conflict) break;
        col++;
      }
      columnById.set(ev.id, col);
    }

    const numColumns = (Array.from(columnById.values()).reduce((max, v) => Math.max(max, v), 0)) + 1;
    const columnWidthPercent = 100 / Math.max(1, numColumns);

    // Step 2: Calculate positions for each event
    const result: CalendarEventWithPosition[] = [];
    
    // Track layering within each column (from calendar component approach)
    const columnLayers = new Map<number, string[]>();
    
    for (const ev of eventsSorted) {
      const evStart = moment(ev.startTime);
      const evEnd = moment(ev.endTime);

      // Calculate top and height positions using actual duration in milliseconds (like calendar component)
      // This approach is more accurate and handles day boundaries correctly
      const selectedDateStart = moment(targetDate).startOf('day');
      const selectedDateEnd = moment(targetDate).endOf('day');
      
      // Calculate the actual start time relative to the selected date
      const eventStartRelative = moment.max(evStart, selectedDateStart);
      const eventEndRelative = moment.min(evEnd, selectedDateEnd);
      
      // Calculate start position in minutes from calendar start hour
      const startMinutes = (eventStartRelative.hour() - this.startHour) * 60 + eventStartRelative.minute();
      
      // Clamp startMinutes to 0 if event starts before the calendar start time
      const clampedStartMinutes = Math.max(0, startMinutes);
      const topPx = (clampedStartMinutes / 60) * this.slotHeight;
      
      // Calculate actual duration in milliseconds (like calendar component)
      // This is more reliable than calculating minutes difference
      let actualDuration: number;
      if (evEnd.isSame(evStart, 'day')) {
        // Same day event - use actual duration
        actualDuration = evEnd.diff(evStart, 'milliseconds');
      } else {
        // Multi-day event - calculate to end of selected day
        actualDuration = selectedDateEnd.diff(eventStartRelative, 'milliseconds');
      }
      
      // Convert milliseconds to pixels
      // slotHeight represents 60 minutes = 3600000 milliseconds
      // So: heightPx = (durationMs / 3600000) * slotHeight
      // Or: heightPx = (durationMs / (60 * 60 * 1000)) * slotHeight
      const durationMs = actualDuration;
      const heightPx = (durationMs / (60 * 60 * 1000)) * this.slotHeight;
      
      // Ensure minimum height for visibility (at least 20px or 15 minutes worth)
      const minHeightPx = Math.max(20, (15 / 60) * this.slotHeight);
      const finalHeightPx = Math.max(minHeightPx, heightPx);

      const col = columnById.get(ev.id) ?? 0;

      // Calculate layer within column (for overlapping events in same column)
      // Track events that have been processed in each column to determine layering
      if (!columnLayers.has(col)) {
        columnLayers.set(col, []);
      }
      const columnEvents = columnLayers.get(col)!;
      
      // Find the maximum layer of overlapping events already processed in this column
      let maxLayer = 0;
      for (const otherId of columnEvents) {
        const other = events.find(e => e.id === otherId);
        if (!other) continue;
        const otherStart = moment(other.startTime);
        const otherEnd = moment(other.endTime);
        
        // Check if events overlap
        if (evStart.isBefore(otherEnd) && evEnd.isAfter(otherStart)) {
          // Find the layer of the overlapping event from results
          const overlappingEvent = result.find(r => r.id === other.id);
          if (overlappingEvent && typeof overlappingEvent.zIndex === 'number') {
            const otherLayer = overlappingEvent.zIndex - 5;
            maxLayer = Math.max(maxLayer, otherLayer + 1);
          }
        }
      }
      
      const layer = maxLayer;
      columnEvents.push(ev.id);

      // Calculate late-overlap indentation (for events that start after first hour of earlier event)
      // This provides visual clarity for complex overlaps
      const lateOverlapLevel = eventsSorted.filter(other => {
        if (other.id === ev.id) return false;
        const otherCol = columnById.get(other.id) ?? -1;
        if (otherCol !== col) return false;
        const otherStart = moment(other.startTime);
        const otherEnd = moment(other.endTime);
        if (otherStart.isAfter(evStart)) return false; // only earlier or equal start
        const activeAtStart = evStart.isBefore(otherEnd);
        if (!activeAtStart) return false;
        // Stack only when overlap starts after the first hour of the earlier event
        return evStart.isSameOrAfter(moment(otherStart).add(1, 'hour'));
      }).length;

      // Use the maximum of layer and lateOverlapLevel for indentation
      const indentLevel = Math.max(layer, lateOverlapLevel);
      const indentPx = indentWidth * indentLevel;

      // Expand width to the right until the next column that has a full-duration overlap
      // This sophisticated width expansion maximizes space usage
      const blockingRightCols: number[] = eventsSorted
        .filter(other => other.id !== ev.id)
        .filter(other => {
          const otherCol = columnById.get(other.id) ?? -1;
          if (otherCol <= col) return false;
          const otherStart = moment(other.startTime);
          const otherEnd = moment(other.endTime);
          return evStart.isBefore(otherEnd) && evEnd.isAfter(otherStart);
        })
        .map(other => columnById.get(other.id) as number);

      const nextBlockingCol = blockingRightCols.length > 0 ? Math.min(...blockingRightCols) : null;
      const lastSpanCol = nextBlockingCol != null ? Math.max(col, nextBlockingCol - 1) : (numColumns - 1);
      const spanColumns = Math.max(1, lastSpanCol - col + 1);

      // Use percentage-based width for responsiveness (from calendar component)
      // But keep pixel-based left offset for the baseLeftOffset
      const leftPercent = col * columnWidthPercent;
      const widthPercent = spanColumns * columnWidthPercent;
      
      // Convert percentage to pixels for left (accounting for baseLeftOffset)
      const leftPx = baseLeftOffset + (leftPercent / 100) * availableWidth + indentPx;
      // Use percentage for width but convert to pixels, accounting for indent
      const widthPx = Math.max(16, (widthPercent / 100) * availableWidth - indentPx);

      const zIndex = 5 + indentLevel;

      result.push({
        ...ev,
        position: {
          top: `${topPx}px`,
          height: `${finalHeightPx}px`,
          left: `${leftPx}px`,
          width: `${widthPx}px`
        },
        zIndex
      });
    }

    return result;
  }

  private isOverlapWithinFirstHour(a: CalendarEvent, b: CalendarEvent): boolean {
    const aStart = moment(a.startTime);
    const aEnd = moment(a.endTime);
    const bStart = moment(b.startTime);
    const bEnd = moment(b.endTime);

    // Determine earlier and later events by start time
    const earlierStartsFirst = aStart.isSameOrBefore(bStart);
    const earlierStart = earlierStartsFirst ? aStart : bStart;
    const earlierEnd = earlierStartsFirst ? aEnd : bEnd;
    const laterStart = earlierStartsFirst ? bStart : aStart;
    const laterEnd = earlierStartsFirst ? bEnd : aEnd;

    // Only consider the first hour window of the earlier event
    const firstHourEnd = moment(earlierStart).add(1, 'hour');

    // Compute actual overlap interval
    const overlapStart = moment.max(earlierStart, laterStart);
    const overlapEnd = moment.min(earlierEnd, laterEnd);

    // Overlap exists and begins within the first hour of the earlier event
    return overlapStart.isBefore(overlapEnd) && overlapStart.isBefore(firstHourEnd);
  }



  private calculateCurrentTimePosition(): number {
    const now = moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    if (currentHour < this.startHour || currentHour > this.endHour) {
      return -1;
    }

    const totalMinutes = (currentHour - this.startHour) * 60 + currentMinute;
    return (totalMinutes / 60) * this.slotHeight;
  }

  selectDate(date: Date | string) {
    // Handle both Date objects and date strings (from swiper-calendar)
    const dateObj = typeof date === 'string' ? moment(date).toDate() : date;
    this.selectedDate.set(dateObj);

    const dateStringForParent = moment(dateObj).format('YYYY-MM-DD');
    this.selectedDateChange.emit(dateStringForParent);
    
    // Sync swiper to the selected date
    this.syncSwiperToSelectedDate();
    
    // Sync swiper-calendar component
    if (this.swiperCalendarComponent) {
      const dateString = typeof date === 'string' ? date : moment(dateObj).format('YYYY-MM-DD');
      this.swiperCalendarComponent.highlightDate(dateString, true);
    }
    
    // If selecting today, scroll to current time
    const isToday = moment(dateObj).isSame(moment().startOf('day'), 'day');
    if (isToday && this.currentTimePosition() >= 0) {
        this.scrollToCurrentTime();
      }
  }
  
  private generateDaySlides() {
    this.daySlides = [];
    const today = new Date();
    const todayMoment = moment().startOf('day');
    
    // Generate slides for 5 weeks (35 days) starting from the closest Monday
    const closestMonday = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    closestMonday.setDate(today.getDate() + diff);
    
    const allEvents = this.events();
    
    for (let i = 0; i < 35; i++) {
      const currentDate = new Date(closestMonday);
      currentDate.setDate(closestMonday.getDate() + i);
      
      const dateString = moment(currentDate).format('YYYY-MM-DD');
      const events = this.getEventsForDate(currentDate, allEvents);
      const isToday = moment(currentDate).isSame(todayMoment, 'day');
      
      this.daySlides.push({
        date: currentDate,
        dateString: dateString,
        events: events.filter(e => !e.isAllDay),
        allDayEvents: this.getAllDayEvents(currentDate, allEvents),
        isToday: isToday
      });
    }
  }
  
  private syncSwiperToSelectedDate() {
    if (!this.daySwiperRef?.nativeElement?.swiper) return;
    
    const selectedDateString = moment(this.selectedDate()).format('YYYY-MM-DD');
    const slideIndex = this.daySlides.findIndex(slide => slide.dateString === selectedDateString);
    
    if (slideIndex !== -1) {
      const swiper = this.daySwiperRef.nativeElement.swiper;
      // Only slide if we're not already on the correct slide
      if (swiper.activeIndex !== slideIndex) {
        swiper.slideTo(slideIndex, 0, false);
      }
    }
  }
  
  activeSlideIndex = signal<number>(0);

  onSwiperSlideChange(event: any) {
    const swiper = event?.detail?.[0];
    if (!swiper || swiper.activeIndex === undefined) return;
    
    const activeIndex = swiper.activeIndex;
    //to keep allDay list updating on date change
    this.activeSlideIndex.set(activeIndex);

    if (activeIndex >= 0 && activeIndex < this.daySlides.length) {
      const selectedSlide = this.daySlides[activeIndex];
      const newDate = selectedSlide.date;
      const newDateString = selectedSlide.dateString;
      
      // Check if we're already on this date to avoid unnecessary updates
      const currentDateString = moment(this.selectedDate()).format('YYYY-MM-DD');
      if (currentDateString === newDateString) return;
      
      // Update selected date
      this.selectedDate.set(newDate);

      this.selectedDateChange.emit(newDateString);
      
      // Sync with swiper-calendar component
      if (this.swiperCalendarComponent) {
        this.swiperCalendarComponent.highlightDate(newDateString, true);
      }
      
      // If selecting today, scroll to current time
      const isToday = moment(newDate).isSame(moment().startOf('day'), 'day');
      if (isToday && this.currentTimePosition() >= 0) {
        setTimeout(() => {
          this.scrollToCurrentTime();
        }, 100);
      }
    }
  }

  @ViewChild(IonContent) content!: IonContent;
  
  selectedEventForPopover: CalendarEvent | null = null;
  selectedSegmentForPopover: string = 'mine';
  isEventPopoverOpen: boolean = false;

  // Booking stepper properties
  selectedBookingStartTime: string | undefined;
  selectedBookingEndTime: string | undefined;

  // Press and hold functionality
  private pressHoldTimer: any = null;
  private pressStartTime: number = 0;
  private pressHoldDuration: number = 1000; // 1 second
  private isPressing: boolean = false;
  private pressStartY: number = 0;
  private pressStartX: number = 0;
  private pressStartRelativeY: number = 0;
  private pressStartRelativeX: number = 0;
  private pressStartEventArea: HTMLElement | null = null;
  private readonly maxMovementThreshold: number = 15; // pixels - cancel if user moves more than this

  async openEventDetails(event: CalendarEvent) {
    this.selectedEventForPopover = event;
      if(this.unleashService.isEnabled(this.unleashFeatureFlags.DAILY_CALENDAR_EVENT_OPENING)) {
        await this.showEditBookingModal(this.selectedEventForPopover);
      }
  }

  closeEventPopover() {
    this.isEventPopoverOpen = false;
    this.selectedEventForPopover = null;
  }

  async closeModal() {
    await this.modalController.dismiss();
  }


  getEventDuration(event: CalendarEvent): number {
    const eventStart = moment(event.startTime);
    const eventEnd = moment(event.endTime);
    return eventEnd.diff(eventStart, 'minutes');
  }

  getEventDurationInHours(event: CalendarEvent): number {
    const eventStart = moment(event.startTime);
    const eventEnd = moment(event.endTime);
    return eventEnd.diff(eventStart, 'hours', true); // true for decimal precision
  }

  shouldShowEventTime(event: CalendarEvent): boolean {
    return this.getEventDuration(event) >= 45;
  }

  shouldShowEventLocation(event: CalendarEvent): boolean {
    return this.getEventDuration(event) >= 60;
  }

  private isUnrespondedInvitation(event: CalendarEvent): boolean {
    // Check if event is an invitation (user is not the organizer)
    if (event.isOrganizer) {
      return false;
    }

    // Get current user's email
    const currentUserEmail = this.userStore.user()?.mail;
    if (!currentUserEmail) {
      return false;
    }

    // Find the current user's attendee record
    const userAttendee = event.attendees?.find(
      (attendee) => attendee.userPrincipalName === currentUserEmail
    );

    if (!userAttendee) {
      return false;
    }

    // Check if the response is 'None' or 'NotResponded' (case-insensitive)
    const response = userAttendee.response?.toLowerCase();
    return response === 'none' || response === 'notresponded';
  }
  
  async showEditBookingModal(calendarEvent) {
    if (this.unleashService.isEnabled(this.unleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: ViewEditBookingComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          attendees: calendarEvent.attendees,
          place: calendarEvent.place,
          meetingToEdit: calendarEvent,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.removeEventFromSlides(data.eventId);
      }
    } else {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: EventComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          finalChoosenAttendees: calendarEvent.attendees,
          place: calendarEvent.place,
          purpose: 'readOnlyBooking',
          meetingToEdit: calendarEvent,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.removeEventFromSlides(data.eventId);
      }
    }
  }

  private removeEventFromSlides(eventId: string) {
    this.daySlides.forEach(slide => {
      slide.events = slide.events.filter(event => event.id !== eventId);
    });
  }

  // Press and hold handlers
  onPressStart(event: MouseEvent | TouchEvent) {
    // Get coordinates
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;

    // Get the event-area element
    const target = event.target as HTMLElement;
    const eventArea = target.closest('.event-area') as HTMLElement;
    if (!eventArea) {
      return;
    }

    // Only check if press is on time slot line or current time line (UI elements, not meetings)
    // Allow press and hold even if there's a meeting underneath
    const timeSlotLine = target.closest('.time-slot-line');
    const currentTimeLine = target.closest('.current-time-line');
    if (timeSlotLine || currentTimeLine) {
      return;
    }

    // Calculate relative position immediately
    const rect = eventArea.getBoundingClientRect();
    const relativeY = clientY - rect.top;
    const relativeX = clientX - rect.left;

    // Check if click is within the event area bounds
    if (relativeY < 0 || relativeY > rect.height || relativeX < 0 || relativeX > rect.width) {
      return;
    }

    // Final check using elementFromPoint - only skip if clicking on UI elements (not meetings)
    const elementAtPoint = document.elementFromPoint(clientX, clientY);
    if (elementAtPoint) {
      const clickedTimeSlotLine = elementAtPoint.closest('.time-slot-line');
      const clickedCurrentTimeLine = elementAtPoint.closest('.current-time-line');
      
      // Only skip if clicking on UI elements, not meetings
      if (clickedTimeSlotLine || clickedCurrentTimeLine) {
        return;
      }
    }

    // Store press information
    this.isPressing = true;
    this.pressStartTime = Date.now();
    this.pressStartY = clientY;
    this.pressStartX = clientX;
    this.pressStartRelativeY = relativeY;
    this.pressStartRelativeX = relativeX;
    this.pressStartEventArea = eventArea;

    // Start timer for 1 second press and hold
    this.pressHoldTimer = setTimeout(() => {
      if (this.isPressing) {
        this.handlePressAndHold();
      }
    }, this.pressHoldDuration);
  }

  onPressEnd(event: MouseEvent | TouchEvent) {
    // Clear the timer if user releases before 1 second
    if (this.pressHoldTimer) {
      clearTimeout(this.pressHoldTimer);
      this.pressHoldTimer = null;
    }
    this.isPressing = false;
    this.pressStartEventArea = null;
  }

  onPressMove(event: MouseEvent | TouchEvent) {
    if (!this.isPressing) return;

    // Get current coordinates
    const clientY = 'touches' in event ? (event.touches[0]?.clientY ?? event.changedTouches[0]?.clientY) : event.clientY;
    const clientX = 'touches' in event ? (event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX) : event.clientX;

    if (clientX === undefined || clientY === undefined) return;

    // Calculate movement distance
    const deltaX = Math.abs(clientX - this.pressStartX);
    const deltaY = Math.abs(clientY - this.pressStartY);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Cancel if user moved too much (likely scrolling or dragging)
    if (distance > this.maxMovementThreshold) {
      if (this.pressHoldTimer) {
        clearTimeout(this.pressHoldTimer);
        this.pressHoldTimer = null;
      }
      this.isPressing = false;
      this.pressStartEventArea = null;
    }
  }

  private handlePressAndHold() {
    // Use stored relative position from when press started
    if (!this.pressStartEventArea) {
      console.log('handlePressAndHold: No event area stored');
      return;
    }

    // Double-check we're not on UI elements (but allow meetings - press and hold works even over meetings)
    const elementAtPoint = document.elementFromPoint(this.pressStartX, this.pressStartY);
    if (elementAtPoint) {
      const timeSlotLine = elementAtPoint.closest('.time-slot-line');
      const currentTimeLine = elementAtPoint.closest('.current-time-line');
      
      // Only skip if clicking on UI elements, not meetings
      if (timeSlotLine || currentTimeLine) {
        return;
      }
    }

    // Calculate which 30-minute time slot was clicked using stored relative Y
    const timeSlot = this.calculateTimeSlotFromYPosition(this.pressStartRelativeY);
    if (!timeSlot) {
      console.warn('Could not calculate time slot from position:', this.pressStartRelativeY);
      return;
    }

    console.log('Opening booking stepper with times:', timeSlot);
    // Open booking stepper with predefined times
    this.openBookingStepperWithTime(timeSlot.startTime, timeSlot.endTime);
  }

  private calculateTimeSlotFromYPosition(yPosition: number): { startTime: string; endTime: string } | null {
    // Calculate which 30-minute slot based on Y position
    // slotHeight represents 60 minutes, so each 30-minute slot is slotHeight / 2
    const minutesPerSlot = 30;
    const pixelsPerMinute = this.slotHeight / 60;
    const totalMinutes = yPosition / pixelsPerMinute;
    
    // Round down to nearest 30-minute slot
    const slotMinutes = Math.floor(totalMinutes / minutesPerSlot) * minutesPerSlot;
    
    // Calculate the hour and minute
    const totalMinutesFromStart = this.startHour * 60 + slotMinutes;
    const hour = Math.floor(totalMinutesFromStart / 60);
    const minute = totalMinutesFromStart % 60;

    // Ensure we're within valid time range
    if (hour < this.startHour || hour > this.endHour) {
      return null;
    }

    // Get the selected date
    const selectedDate = moment(this.selectedDate());
    
    // Create start time (current 30-minute slot)
    const startTime = moment(selectedDate)
      .set('hour', hour)
      .set('minute', minute)
      .set('second', 0)
      .set('millisecond', 0);

    // Create end time (1 hour later, not 30 minutes)
    const endTime = moment(startTime).add(1, 'hour');

    return {
      startTime: startTime.format(DATETIMEFORMART),
      endTime: endTime.format(DATETIMEFORMART)
    };
  }

  private async openBookingStepperWithTime(startTime: string, endTime: string) {
    // Set the initial times for the booking stepper
    this.selectedBookingStartTime = startTime;
    this.selectedBookingEndTime = endTime;
    
    // Trigger the modal by clicking the hidden button
    const triggerButton = document.getElementById('open-booking-stepper-from-calendar');
    if (triggerButton) {
      triggerButton.click();
    }
  }
  private getAllDayEvents(date: Date, events: CalendarEvent[]): any[] {
    const startOfDay = moment(date).startOf('day');
    const endOfDay = moment(date).endOf('day');
  
    let allDayEvents = events.filter(event => {
      if (!event.isAllDay) return false;
      const eventStart = moment(event.startTime);
      const eventEnd = moment(event.endTime);
  
      return eventStart.isBefore(endOfDay) &&
             eventEnd.isAfter(startOfDay);
    });
    return allDayEvents.map((allDayEvent: CalendarEventWithPosition) => {
      const isUnrespondedInvitation = this.isUnrespondedInvitation(allDayEvent);
      return {
        ...allDayEvent,
        color: allDayEvent.isOrganizer ? '#4CAF50' : '#ed143d',
        backgroundColor: allDayEvent.isOrganizer ? '#4CAF5022' : '#ed143d22',
        location: allDayEvent.place?.displayName || 'No location',
        showTime: this.shouldShowEventTime(allDayEvent),
        showLocation: this.shouldShowEventLocation(allDayEvent),
        isUnrespondedInvitation: isUnrespondedInvitation,
      }
    });
  }
}
